<div class="terms-container">

  <h1>Terms of Service</h1>
  <p class="terms-meta">Last Updated: May 2026</p>
  <p>
    These Terms of Service ("Terms") are a legal agreement between you and
    <strong>xoomtag llc</strong>, doing business as <strong>Chareety</strong>
    ("Chareety," "we," "us," or "our"). By accessing or using our services, you
    agree to be bound by these Terms. If you do not agree, do not use our
    services.
  </p>

  <h2>Table of Contents</h2>
  <ol class="toc">
    <li><a (click)="scrollTo('agreement')">Agreement to Terms</a></li>
    <li><a (click)="scrollTo('services')">Description of Services</a></li>
    <li><a (click)="scrollTo('accounts')">Donor Accounts</a></li>
    <li><a (click)="scrollTo('donations')">Donations</a></li>
    <li><a (click)="scrollTo('ip')">Intellectual Property</a></li>
    <li><a (click)="scrollTo('prohibited')">Prohibited Activities</a></li>
    <li><a (click)="scrollTo('privacy')">Privacy</a></li>
    <li><a (click)="scrollTo('third-party')">Third-Party Services</a></li>
    <li><a (click)="scrollTo('disclaimers')">Disclaimers</a></li>
    <li><a (click)="scrollTo('liability')">Limitation of Liability</a></li>
    <li><a (click)="scrollTo('indemnification')">Indemnification</a></li>
    <li><a (click)="scrollTo('governing-law')">Governing Law</a></li>
    <li><a (click)="scrollTo('disputes')">Dispute Resolution</a></li>
    <li><a (click)="scrollTo('changes')">Changes to Terms</a></li>
    <li><a (click)="scrollTo('termination')">Termination</a></li>
    <li><a (click)="scrollTo('contact')">Contact Information</a></li>
    <li><a (click)="scrollTo('general')">General Provisions</a></li>
  </ol>

  <!-- Section 1 -->
  <h2 id="agreement">1. Agreement to Terms</h2>
  <p>
    By accessing or using any Chareety service — including our website at
    <strong>chareety.com</strong>, the donor self-service portal at
    <strong>my.chareety.com</strong> ("MyChareety"), the DonationKiosk
    application, or any other Chareety-branded service — you acknowledge that you
    have read, understood, and agree to be bound by these Terms and our
    <a routerLink="/privacy">Privacy Policy</a>, which is incorporated herein by
    reference.
  </p>
  <h3>1.1 Eligibility</h3>
  <p>
    You must be at least <strong>18 years of age</strong> to use our services. By
    using Chareety, you represent and warrant that you are 18 years of age or
    older and have the legal capacity to enter into these Terms.
  </p>
  <h3>1.2 Geographic Limitation</h3>
  <p>
    Chareety services are available only within the <strong>United States</strong>.
    By using our services, you represent that you are located in the United States
    or are otherwise authorized to use services offered within the United States.
  </p>

  <!-- Section 2 -->
  <h2 id="services">2. Description of Services</h2>
  <p>
    Chareety is a nonprofit SaaS platform that helps organizations collect and
    manage donations. Our services include:
  </p>
  <ul>
    <li>
      <strong>chareety.com</strong> — Our public marketing website providing
      information about the Chareety platform and participating organizations.
    </li>
    <li>
      <strong>MyChareety (my.chareety.com)</strong> — A donor self-service portal
      where donors can view their donation history, manage recurring donations,
      access tax receipts, and update account information.
    </li>
    <li>
      <strong>DonationKiosk</strong> — A tablet-based donation collection
      application used at organization locations for in-person donations.
    </li>
    <li>
      <strong>Donation Processing</strong> — Secure payment processing via Stripe
      on behalf of participating nonprofit organizations.
    </li>
    <li>
      <strong>Recurring Donations</strong> — Subscription-based donation
      management, allowing donors to set up and manage recurring contributions.
    </li>
  </ul>
  <p>
    These Terms govern your use of the services listed above as a donor or
    website visitor. Organizations using the Chareety platform are subject to a
    separate Master Service Agreement. Partners are subject to a separate Partner
    Agreement.
  </p>

  <!-- Section 3 -->
  <h2 id="accounts">3. Donor Accounts</h2>
  <h3>3.1 Registration</h3>
  <p>
    To access certain features of our services, such as MyChareety, you may need
    to create an account. When you register, you agree to provide accurate,
    current, and complete information and to update such information as necessary
    to keep it accurate, current, and complete.
  </p>
  <h3>3.2 Account Security</h3>
  <p>
    You are responsible for maintaining the confidentiality of your account
    credentials, including your password. You agree to notify us immediately of
    any unauthorized use of your account or any other breach of security. Chareety
    is not liable for any loss or damage arising from your failure to protect your
    account credentials.
  </p>
  <h3>3.3 Accurate Information</h3>
  <p>
    You represent and warrant that all information you provide to Chareety,
    including during account registration and when making donations, is truthful,
    accurate, and complete. Providing false or misleading information may result
    in suspension or termination of your account.
  </p>

  <!-- Section 4 -->
  <h2 id="donations">4. Donations</h2>
  <h3>4.1 No Refunds</h3>
  <p>
    All donations made through the Chareety platform are <strong>final and
    non-refundable</strong>. By completing a donation, you acknowledge and agree
    that you will not seek a chargeback, refund, or reversal of any donation
    through your bank or payment provider except in cases of unauthorized or
    fraudulent transactions.
  </p>
  <h3>4.2 Payment Processing</h3>
  <p>
    Donations are processed by <strong>Stripe</strong> on behalf of the receiving
    nonprofit organization. Chareety is not the recipient of your donation — the
    organization you select is the recipient. Chareety acts as a technology
    platform facilitating the transaction between you and the organization.
  </p>
  <h3>4.3 Platform Fee</h3>
  <p>
    Chareety charges a platform fee for its services, which may be included in or
    added to your donation amount. This fee is disclosed transparently during the
    donation process before you confirm your contribution.
  </p>
  <h3>4.4 Tax Deductibility</h3>
  <p>
    Chareety provides donation receipts for your records; however,
    <strong>Chareety does not guarantee the tax-deductible status</strong> of any
    donation. Tax deductibility depends on the receiving organization's tax-exempt
    status and applicable tax laws. It is your responsibility, in consultation
    with your tax advisor, to determine whether your donation qualifies as a
    tax-deductible contribution. The accuracy of tax receipts is the
    responsibility of the receiving organization.
  </p>
  <h3>4.5 Recurring Donations</h3>
  <p>
    If you set up a recurring donation (subscription), your payment method will
    be charged automatically at the interval you select until you cancel. You may
    cancel a recurring donation at any time through MyChareety or by contacting
    the receiving organization directly. Cancellation will take effect before the
    next scheduled charge.
  </p>

  <!-- Section 5 -->
  <h2 id="ip">5. Intellectual Property</h2>
  <h3>5.1 Ownership</h3>
  <p>
    The Chareety platform, including all software, designs, text, graphics,
    logos, trademarks, service marks, and other content (collectively,
    "Chareety Content"), is owned by or licensed to xoomtag llc and is protected
    by copyright, trademark, and other intellectual property laws.
  </p>
  <h3>5.2 Limited License</h3>
  <p>
    Subject to your compliance with these Terms, Chareety grants you a limited,
    non-exclusive, non-transferable, revocable license to access and use the
    services for your personal, non-commercial purposes (i.e., making donations
    and managing your donor account). This license does not include the right to
    modify, reproduce, distribute, create derivative works from, publicly
    display, or commercially exploit any Chareety Content.
  </p>

  <!-- Section 6 -->
  <h2 id="prohibited">6. Prohibited Activities</h2>
  <p>
    You agree not to engage in any of the following prohibited activities:
  </p>
  <ul>
    <li>
      <strong>Fraudulent Donations</strong> — Making donations using stolen,
      unauthorized, or fraudulent payment methods.
    </li>
    <li>
      <strong>Unauthorized Access</strong> — Attempting to access accounts,
      systems, or data that you are not authorized to access, including through
      hacking, password mining, or any other means.
    </li>
    <li>
      <strong>Scraping and Data Collection</strong> — Using automated tools,
      bots, spiders, scrapers, or similar technologies to collect data from the
      platform without our express written permission.
    </li>
    <li>
      <strong>Interference</strong> — Interfering with or disrupting the
      integrity or performance of the platform or its underlying infrastructure,
      including transmitting viruses, malware, or other harmful code.
    </li>
    <li>
      <strong>Impersonation</strong> — Impersonating any person or entity, or
      falsely stating or otherwise misrepresenting your affiliation with any
      person or entity.
    </li>
    <li>
      <strong>Illegal Activity</strong> — Using the platform for any purpose that
      is unlawful or prohibited by these Terms.
    </li>
  </ul>

  <!-- Section 7 -->
  <h2 id="privacy">7. Privacy</h2>
  <h3>7.1 Privacy Policy</h3>
  <p>
    Your use of the Chareety platform is also governed by our
    <a routerLink="/privacy">Privacy Policy</a>, which describes how we collect,
    use, store, and disclose your personal information. By using our services,
    you consent to the practices described in our Privacy Policy.
  </p>
  <h3>7.2 reCAPTCHA Enterprise</h3>
  <p>
    This site is protected by reCAPTCHA Enterprise and the Google
    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
    and
    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
    apply. reCAPTCHA Enterprise is used to verify that interactions with our
    platform are from legitimate users and not automated bots.
  </p>

  <!-- Section 8 -->
  <h2 id="third-party">8. Third-Party Services</h2>
  <h3>8.1 Stripe</h3>
  <p>
    Payment processing is provided by Stripe, Inc. Your use of Stripe's payment
    services is subject to the
    <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">Stripe Terms of Service</a>
    and the
    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>.
    By making a donation through Chareety, you agree to be bound by Stripe's
    terms as they apply to your transaction.
  </p>
  <h3>8.2 Google Cloud</h3>
  <p>
    The Chareety platform is hosted on Google Cloud infrastructure. While we take
    reasonable measures to ensure the security and availability of our platform,
    service availability is subject to Google Cloud's operational status.
  </p>
  <h3>8.3 Third-Party Links</h3>
  <p>
    Our services may contain links to third-party websites or resources. These
    links are provided for your convenience only. Chareety does not endorse and
    is not responsible for the content, products, or services offered by
    third-party sites. Your interactions with third-party websites are solely
    between you and the third party.
  </p>

  <!-- Section 9 -->
  <h2 id="disclaimers">9. Disclaimers</h2>
  <p>
    THE CHAREETY PLATFORM AND ALL SERVICES ARE PROVIDED <strong>"AS IS"</strong>
    AND <strong>"AS AVAILABLE"</strong> WITHOUT WARRANTIES OF ANY KIND, EITHER
    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
    NON-INFRINGEMENT.
  </p>
  <p>
    WITHOUT LIMITING THE FOREGOING, CHAREETY DOES NOT WARRANT THAT:
  </p>
  <ul>
    <li>
      The services will be uninterrupted, timely, secure, or error-free.
    </li>
    <li>
      The results obtained from using the services will be accurate or reliable.
    </li>
    <li>
      Any organization listed on the platform will use donated funds in any
      particular manner. Chareety is not responsible for how organizations
      allocate or spend donations received through the platform.
    </li>
    <li>
      Tax receipts provided through the platform are accurate. The accuracy and
      completeness of tax receipts is the responsibility of the receiving
      organization.
    </li>
  </ul>

  <!-- Section 10 -->
  <h2 id="liability">10. Limitation of Liability</h2>
  <p>
    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
    CHAREETY, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE
    LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
    DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL,
    OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
  </p>
  <ul>
    <li>Your access to or use of (or inability to access or use) the services.</li>
    <li>Any conduct or content of any third party on the services.</li>
    <li>Any content obtained from the services.</li>
    <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
  </ul>
  <p>
    IN NO EVENT SHALL CHAREETY'S TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS
    ARISING FROM OR RELATED TO THESE TERMS OR YOUR USE OF THE SERVICES EXCEED THE
    GREATER OF: (A) THE TOTAL AMOUNT OF DONATIONS YOU HAVE MADE THROUGH THE
    PLATFORM IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED
    DOLLARS ($100.00).
  </p>

  <!-- Section 11 -->
  <h2 id="indemnification">11. Indemnification</h2>
  <p>
    You agree to defend, indemnify, and hold harmless Chareety, xoomtag llc,
    and their respective officers, directors, employees, agents, and affiliates
    from and against any and all claims, damages, obligations, losses,
    liabilities, costs, and expenses (including reasonable attorneys' fees)
    arising from or related to: (a) your use of the services; (b) your violation
    of these Terms; (c) your violation of any third-party right, including any
    intellectual property, privacy, or proprietary right; or (d) any claim that
    your use of the services caused damage to a third party.
  </p>

  <!-- Section 12 -->
  <h2 id="governing-law">12. Governing Law</h2>
  <p>
    These Terms shall be governed by and construed in accordance with the laws of
    the <strong>State of Illinois</strong>, without regard to its conflict of law
    provisions. Any legal action or proceeding not subject to arbitration under
    Section 13 shall be brought exclusively in the state or federal courts located
    in <strong>DuPage County, Illinois</strong>, and you consent to the personal
    jurisdiction of such courts.
  </p>

  <!-- Section 13 -->
  <h2 id="disputes">13. Dispute Resolution</h2>
  <h3>13.1 Informal Resolution</h3>
  <p>
    Before filing any formal legal action, you agree to first attempt to resolve
    any dispute, claim, or controversy arising out of or relating to these Terms
    or the services ("Dispute") informally by contacting us at
    <a href="mailto:support@chareety.com">support&#64;chareety.com</a>. We will
    attempt to resolve the Dispute informally within <strong>thirty (30)
    days</strong> of receiving your notice. If the Dispute is not resolved within
    that period, either party may proceed as set forth below.
  </p>
  <h3>13.2 Binding Arbitration</h3>
  <p>
    If informal resolution is unsuccessful, any remaining Dispute shall be
    resolved by <strong>binding arbitration</strong> administered by the American
    Arbitration Association ("AAA") in accordance with its Consumer Arbitration
    Rules then in effect. The arbitration shall be conducted in DuPage County,
    Illinois, or at another mutually agreed location. The arbitrator's decision
    shall be final and binding and may be entered as a judgment in any court of
    competent jurisdiction.
  </p>
  <h3>13.3 Small Claims Court Exception</h3>
  <p>
    Notwithstanding the foregoing, either party may bring an individual action in
    small claims court for Disputes within the jurisdiction of such court.
  </p>
  <h3>13.4 No Class Actions</h3>
  <p>
    YOU AND CHAREETY AGREE THAT EACH PARTY MAY BRING DISPUTES AGAINST THE OTHER
    ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY
    PURPORTED CLASS ACTION, COLLECTIVE ACTION, OR REPRESENTATIVE PROCEEDING. The
    arbitrator may not consolidate more than one person's claims and may not
    preside over any form of representative or class proceeding.
  </p>

  <!-- Section 14 -->
  <h2 id="changes">14. Changes to Terms</h2>
  <p>
    Chareety reserves the right to modify or replace these Terms at any time at
    our sole discretion. If we make material changes, we will provide notice by
    updating the "Last Updated" date at the top of these Terms and, where
    appropriate, providing additional notice (such as via email or a prominent
    notice on our website). Your continued use of the services after any changes
    to these Terms constitutes your acceptance of the revised Terms. If you do
    not agree to the revised Terms, you must stop using the services.
  </p>

  <!-- Section 15 -->
  <h2 id="termination">15. Termination</h2>
  <h3>15.1 Termination by Chareety</h3>
  <p>
    We may suspend or terminate your account and access to the services at any
    time, with or without cause and with or without notice, including if we
    reasonably believe you have violated these Terms. Upon termination, your
    right to use the services will immediately cease.
  </p>
  <h3>15.2 Termination by You</h3>
  <p>
    You may delete your account at any time by contacting us at
    <a href="mailto:support@chareety.com">support&#64;chareety.com</a> or through
    the account settings in MyChareety. Upon account deletion, your personal
    information will be handled in accordance with our
    <a routerLink="/privacy">Privacy Policy</a>.
  </p>
  <h3>15.3 Survival</h3>
  <p>
    The following sections shall survive termination of these Terms: Sections 4
    (Donations), 5 (Intellectual Property), 9 (Disclaimers), 10 (Limitation of
    Liability), 11 (Indemnification), 12 (Governing Law), 13 (Dispute
    Resolution), and 17 (General Provisions).
  </p>

  <!-- Section 16 -->
  <h2 id="contact">16. Contact Information</h2>
  <p>
    If you have any questions about these Terms, please contact us:
  </p>
  <div class="contact-info">
    <p>
      <strong>xoomtag llc, d/b/a Chareety</strong><br>
      Bartlett, IL 60103<br>
      Bartlett, IL 60103
    </p>
    <p>
      General Inquiries:
      <a href="mailto:support@chareety.com">support&#64;chareety.com</a>
    </p>
    <p>
      Privacy Inquiries:
      <a href="mailto:privacy@chareety.com">privacy&#64;chareety.com</a>
    </p>
  </div>

  <!-- Section 17 -->
  <h2 id="general">17. General Provisions</h2>
  <h3>17.1 Severability</h3>
  <p>
    If any provision of these Terms is held to be invalid, illegal, or
    unenforceable by a court of competent jurisdiction, the remaining provisions
    shall remain in full force and effect. The invalid or unenforceable provision
    shall be modified to the minimum extent necessary to make it valid and
    enforceable while preserving its original intent.
  </p>
  <h3>17.2 Waiver</h3>
  <p>
    The failure of Chareety to enforce any right or provision of these Terms
    shall not constitute a waiver of such right or provision. Any waiver of any
    provision of these Terms will be effective only if in writing and signed by
    Chareety.
  </p>
  <h3>17.3 Assignment</h3>
  <p>
    You may not assign or transfer these Terms, or any rights or obligations
    hereunder, without the prior written consent of Chareety. Chareety may assign
    or transfer these Terms, in whole or in part, without restriction and without
    notice to you.
  </p>
  <h3>17.4 Entire Agreement</h3>
  <p>
    These Terms, together with the Privacy Policy and any other agreements
    expressly incorporated by reference herein, constitute the entire agreement
    between you and Chareety regarding your use of the services and supersede all
    prior and contemporaneous agreements, understandings, and communications,
    whether written or oral.
  </p>
  <h3>17.5 Force Majeure</h3>
  <p>
    Chareety shall not be liable for any failure or delay in performing its
    obligations under these Terms where such failure or delay results from causes
    beyond its reasonable control, including but not limited to acts of God,
    natural disasters, pandemics, war, terrorism, labor disputes, government
    actions, power failures, internet or telecommunications failures, or
    third-party service outages.
  </p>

</div>
